// Editor-wide state the tab bodies and the shared field components read
// without prop drilling: the server payload (core, instructors, env), the
// user's landing roles, tab navigation with focus, the upload counter that
// keeps Save disabled while a file is still going up, and the structure bus
// that retires a delete's Undo once a list was rearranged (plan R6 DL-5).
import { createContext, useContext } from 'react';
import type {
  InstructorOption,
  LandingEditorCore,
  LandingEditorPayload,
  LandingTab,
} from 'src/@types/landing/landing.contract';
import type { RenderReasonContext } from 'src/@types/landing/landing.logic';
import type { LandingRoles } from './rules/roles';
import type { StructureBus } from './rules/structureBus';

export type LandingEditorContextValue = {
  diplomaId: string;
  payload: LandingEditorPayload;
  core: LandingEditorCore;
  instructorOptions: InstructorOption[];
  demoAllowed: boolean;
  roles: LandingRoles;
  // Switch tab and (optionally) scroll to + focus a form path in it.
  goTo: (tab: LandingTab, path?: string) => void;
  // Navigate out of the editor, asking first when there are unsaved edits.
  leave: (path: string) => void;
  // Register an upload; call the returned function when it ends.
  beginUpload: () => () => void;
  uploading: boolean;
  // Bumped by every move, insert, remove, duplicate and undo of a list.
  structure: StructureBus;
  // Re-read core/computed/readiness after the Diploma settings modal saved.
  reloadServerState: () => Promise<void>;
  // Opens the core modal in place (admin / customer-care only).
  openDiplomaSettings: () => void;
  // Opens Import from JSON (admin / customer-care only).
  openImport: () => void;
};

export const LandingEditorContext = createContext<LandingEditorContextValue | null>(null);

export function useLandingEditorContext(): LandingEditorContextValue {
  const ctx = useContext(LandingEditorContext);
  if (!ctx) throw new Error('useLandingEditorContext must be used inside the landing editor');
  return ctx;
}

/** itemRenderReason context: demo items count only where demo is allowed. */
export function renderReasonContext(ctx: LandingEditorContextValue): RenderReasonContext {
  return { demoShown: ctx.demoAllowed, price: ctx.core.price, now: new Date() };
}

/** DOM id for a form path, used by goTo() to scroll to and focus a field. */
export const fieldDomId = (path: string) => `lf-${path.replace(/[^A-Za-z0-9_-]/g, '-')}`;
